/**
 * Main source of the client react system
 * 	- Stores & Create routes & pages
 * 	- Auto navigate logic
 *  - Check logins & tokens
 *  -
 */

import { Alert, ConfigProvider, theme } from "antd";
import jwtDecode from "jwt-decode";
import { Fragment, useEffect, useState } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
} from "react-router-dom";
import "./App.scss";
import ProtectedModuleRoute from "./components/ProtectedModuleRoute";
import TenantSwitchBoundary from "./components/TenantSwitchBoundary";
import Home from "./containers/Home";
import LoginPage from "./containers/LoginPage";
import NoAccess from "./containers/NoAccess";
import HelpCentreConfigurationRoute from "./pages/help-centre/HelpCentreConfigurationRoute";
import HelpCentreHome from "./pages/help-centre/HelpCentreHome";
import HelpCentreLayout from "./pages/help-centre/HelpCentreLayout";
import HelpCentreTopic from "./pages/help-centre/HelpCentreTopic";
import HelpCentreTopics from "./pages/help-centre/HelpCentreTopics";
import { ThemeContext } from "./contexts/context";
import useTheme from "./hooks/useTheme";
import { getAppRoles } from "./services/api-server/graphql";
import { getSpecificTenants } from "./services/api-server/tenant_management";
import { getLastTenant, getMe } from "./services/api-server/user";
import { refreshToken } from "./services/api-server/usertoken";
import LoginSuccess from "./services/LoginSuccess";
import { MainMenuItems } from "./services/MainMenu";
import { superAdminRole, url } from "./utils/_exports";
import { GetAntIcon } from "./utils/ant_icons";
import { setContainer } from "./utils/containers";
import Emitter from "./utils/emitter";
import { pickInitialTenant } from "./utils/initialTenant";
import Location from "./utils/location";
// Opens the server socket as the app starts.
import "./utils/socket";
import { includesIgnoreCase, logout } from "./utils/utils";
import { useUserStore } from "./stores/userStore";
import { useTenantStore } from "./stores/tenantStore";

export const Mode = `${import.meta.env.VITE_MODE}`;
export const Tenant = `elemental`;

const { defaultAlgorithm, darkAlgorithm } = theme;

let tokenCheck: any = null;

const App = () => {
	const setUserData = useUserStore((state) => state.setUserData);
	const setTenantList = useTenantStore((state) => state.setTenantList);

	let isLogin = false;
	let shouldRedirect = false;
	let timeout: any = null;

	const restricted = new Set([
		"",
		"/",
		"/loginsuccess",
		"/loginsuccess/",
		"/logout",
		"/logout/",
	]);

	const { initialMainMenu, initialExtraMenu, otherMenu } = MainMenuItems();

	const [user, setUser] = useState<any>(null);
	const [userRole, setUserRole] = useState<any>([]);
	const [menuRoutes, setMenuRoutes] = useState<[]>([]);
	const [adminMenuRoutes, setAdminMenuRoutes] = useState<[]>([]);
	const [currentLocation, setCurrentLocation] = useState<any>(
		window.location.pathname
	);
	const [accessTokenDecoded, setAccessTokenDecoded] = useState<any>(null);
	const [idTokenDecoded, setIDTokenDecoded] = useState<any>(null);

	const [alert, setAlert] = useState<any>(null);

	const [tenantDataList, setTenantDataList] = useState<any>(null);
	const [initialLoad, setInitialLoad] = useState<any>(true);

	// Theme related states
	const [defaultTheme, setDefaultTheme] = useState<boolean>(true);
	const [theme, setTheme] = useState<any>();
	const [themeVariables, setThemeVariables] = useState<any>(null);

	const { setLightDarkMode, setCustomTheme } = useTheme();

	// localStorage first: many components read the theme from it as they render.
	const applyMode = (mode: "light" | "dark") => {
		localStorage.setItem("theme", mode);
		setTheme(mode);
	};

	// This is to define the current url location of the user
	// This is mostly use to define
	const onLocationChange = (location: Location) => {
		if (initialLoad) {
			setInitialLoad(false);
		} else {
			localStorage.removeItem("selectedFilters");
		}
		setCurrentLocation(location.pathname);
	};

	// Quick simple check for sign-ins
	localStorage.removeItem("isLogin");
	if (!restricted.has(currentLocation)) {
		if (
			(!localStorage.getItem("accessToken") &&
				!localStorage.getItem("idToken")) ||
			localStorage.getItem("accessToken") === "undefined" ||
			localStorage.getItem("idToken") === "undefined"
		) {
			localStorage.removeItem("accessToken");
			localStorage.removeItem("idToken");
			shouldRedirect = true;
		} else {
			isLogin = true;
		}
	}

	// Get the signed-in user's own record, and populate user info into the App.
	// The server works out who they are from the sign-in, including a guest
	// invited with an email alias, and creates the record on a first sign-in.
	const getUserInfo = () => {
		getMe()
			.then((userData: any) => {
				setUser(userData);
				setUserData(userData, userRole);
				applyMode(userData?.theme || "dark"); // NOSONAR: an empty stored theme falls back to dark
			})
			.catch((error: any) => {
				// The message only: a request error carries the bearer token.
				console.log(error?.message);
				logout(true);
			});
	};

	// Emitter controller - Control events within the app itself
	const emitterController = () => {
		Emitter.on("userSaved", () => {
			getUserInfo();
		});

		Emitter.on("alert", (payload: any) => {
			if (payload) {
				if (payload.timeout) {
					if (timeout) {
						clearTimeout(timeout);
						timeout = null;
					}
					timeout = setTimeout(() => {
						setAlert(null);
					}, payload.timeout);
				}
				setAlert({
					type: payload.type,
					message: payload.message,
					description: payload.description,
					top: payload.top,
					closeable: payload.closable,
				});
			} else {
				setAlert(null);
			}
		});
	};

	// Token Controller - to check initial token expiry and set tokens for user info usage
	const tokenController = (
		email: any,
		idTokenDecoded: any,
		accessTokenDecoded: any
	) => {
		let currentDate = Date.now() / 1000;
		if (idTokenDecoded && accessTokenDecoded) {
			if (currentDate > accessTokenDecoded?.exp - 5 * 60) {
				if (currentDate > accessTokenDecoded?.exp + 30 * 60) {
					logout(true);
				} else {
					//RefreshToken
					refreshToken(email)
						.then((refreshed_token: any) => {
							setAccessTokenDecoded(jwtDecode(refreshed_token));
							Emitter.emit("refreshed", null);
						})
						.catch(() => {
							logout(true);
						});
				}
			}
		} else if (!idTokenDecoded) {
			alert("NO ID TOKEN FOUND");
			logout(true);
		} else if (!accessTokenDecoded) {
			alert("NO ACCESS TOKEN FOUND");
			logout(true);
		} else {
			alert("You have been log out for unknown reasons");
			logout(true);
		}
	};

	// Check Login && Redirect
	useEffect(() => {
		if (!restricted.has(currentLocation) && !user)
			try {
				const idToken_decoded: any = jwtDecode(
					localStorage.getItem("idToken") ?? ""
				);
				const accessToken_decoded: any = jwtDecode(
					localStorage.getItem("accessToken") ?? ""
				);

				emitterController();
				tokenController(
					idToken_decoded.preferred_username,
					idToken_decoded,
					accessToken_decoded
				);
				setAccessTokenDecoded(accessToken_decoded);
				setIDTokenDecoded(idToken_decoded);
				getUserInfo();
			} catch {
				logout(true);
			}
	}, [currentLocation]);

	// Interval check for token expiry of users
	useEffect(() => {
		if (tokenCheck) clearInterval(tokenCheck);

		if (idTokenDecoded && accessTokenDecoded)
			tokenCheck = setInterval(() => {
				tokenController(
					idTokenDecoded?.preferred_username,
					idTokenDecoded,
					accessTokenDecoded
				);
			}, 60000);
	}, [accessTokenDecoded, idTokenDecoded]);

	// Load Menus and populate routes
	useEffect(() => {
		if (user && tenantDataList) {
			let allMenu: any = [
				...initialMainMenu,
				...initialExtraMenu,
				...otherMenu,
			];

			const menuRoutes: any = [];
			const adminMenuRoutes: any = [];
			let params: any = { user, userRole, tenantDataList };
			// Function
			const traverse = (menuItems: any, parentPath: any = "") => {
				menuItems.forEach((item: any) => {
					let route = null;
					if (item.to) {
						if (item.requires_admin) {
							const isTenantAdminAnywhere = tenantDataList?.some(
								(tenant: any) =>
									includesIgnoreCase(tenant.tenant_admin, user?.email)
							);
							if (
								// If user is super admin or tenant admin in any tenant
								params?.userRole?.includes(superAdminRole) ||
								isTenantAdminAnywhere
							) {
								route = (
									<>
										<Route
											key={item.key}
											// path={"admin/" + item.to.split("/").pop()}
											path={item.to.split("/").pop()}
											element={setContainer(item.container, {
												...params,
												...item,
												user: user,
											})}
										/>
										{item.container === "admin" && (
											<>
												<Route
													key={item.key}
													path={
														item.to.split("/").pop() + "/programme-settings"
													}
													element={
														<ProtectedModuleRoute moduleKey="project-delivered">
															{setContainer("wellProgrammeTemplate", {
																key: "well-programme-template",
																label: "Well Programme Template",
																proptitle: "Well Programme Template",
																container: "Well Programme Template",
																icon: GetAntIcon("project"),
																requires_admin: true,
																...params,
																userRole,
																user: user,
															})}
														</ProtectedModuleRoute>
													}
												/>
												<Route
													key={item.key}
													path={
														item.to.split("/").pop() + "/tenant-configuration"
													}
													element={setContainer("tenant-configuration", {
														key: "tenant-configuration",
														label: "Tenant Configuration",
														proptitle: "Tenant Configuration",
														container: "tenant-configuration",
														icon: GetAntIcon("project"),
														requires_admin: true,
														...params,
														userRole,
														user: user,
													})}
												/>
												<Route
													key={item.key}
													path={
														item.to.split("/").pop() +
														"/tenant-configuration/module-roles"
													}
													element={setContainer("moduleRoles", {
														key: "module-roles",
														label: "Module Roles",
														proptitle: "Module Roles",
														container: "moduleRoles",
														icon: GetAntIcon("project"),
														requires_admin: true,
														...params,
														userRole,
														user: user,
													})}
												/>
											</>
										)}
									</>
								);
							} else {
								route = (
									<Route
										key={item.key}
										// path={"admin/" + item.to.split("/").pop()}
										path={item.to.split("/").pop()}
										element={
											<NoAccess
												break={true}
												text={
													"Oops, looks like you don't have the authorisation to view this page."
												}
											/>
										}
									/>
								);
							}
						} else {
							if (item.container === "projects") {
								route = (
									<>
										{/* Main Project Route */}
										<Route
											key={item.key}
											path={item.to.split("/").pop()}
											element={
												<ProtectedModuleRoute moduleKey="core">
													{setContainer(item.container, {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>
										{/* Project Overview Route */}
										<Route
											key={"projectsOverview"}
											path={item.to.split("/").pop() + "/:projectname"}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("projectCampaignOverview", {
															key: "project-menu",
															label: "Project Overview",
															proptitle: "Project Overview",
															container: "projectCampaignOverview",
															icon: GetAntIcon("project"),
															to: `/overview`,
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Add New Project Route */}
										<Route
											key={"addNewProject"}
											path={item.to.split("/").pop() + "/add-new-project"}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("projectSettings", {
															key: "project-menu",
															label: "projectSettings",
															proptitle: "projectSettings",
															container: "projectSettings",
															icon: GetAntIcon("project"),
															to: "/projects",
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Project Settings Route */}
										<Route
											key={"projects"}
											path={
												item.to.split("/").pop() +
												"/:projectname/project-settings"
											}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("projectSettings", {
															key: "project-menu",
															label: "projectSettings",
															proptitle: "projectSettings",
															container: "projectSettings",
															icon: GetAntIcon("project"),
															to: "/projects",
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"projects"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:operationname"
											}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("WellOverview", {
															key: "project-well-overview",
															label: "wellOverview",
															proptitle: "wellOverview",
															container: "wellOverview",
															icon: GetAntIcon("project"),
															to: "/projects",
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"projects"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:wellname/operation-settings"
											}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("OperationSettings", {
															key: "project-well-settings",
															label: "OperationSettings",
															proptitle: "OperationSettings",
															container: "OperationSettings",
															icon: GetAntIcon("project"),
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"projects"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:operationname/:phasename"
											}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("PhaseOverview", {
															key: "task-overview",
															label: "phaseOverview",
															proptitle: "phaseOverview",
															container: "phaseOverview",
															icon: GetAntIcon("project"),
															to: "/projects",
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"projects"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:wellname/:phasename/:taskname"
											}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("TaskOverview", {
															key: "task-overview",
															label: "taskOverview",
															proptitle: "taskOverview",
															container: "taskOverview",
															icon: GetAntIcon("project"),
															to: "/projects",
															requires_admin: true,
															...params,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:operationname/preview"
											}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"wellExaminationPreview"}
											path={
												item.to.split("/").pop() +
												"/:projectname/well-examination/preview"
											}
											element={
												<ProtectedModuleRoute moduleKey="core">
													<TenantSwitchBoundary>
														{setContainer("wellExaminationPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/:projectname/:operationname/:phasename/:taskname/preview"
											}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"multi-asset"}
											path={
												item.to.split("/").pop() +
												"/:project_code/multi-asset/:phase_id"
											}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("multi-asset", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
									</>
								);
							} else if (item.container === "wells") {
								route = (
									<>
										<Route
											key={item.key}
											path={item.to.split("/").pop()}
											element={
												<ProtectedModuleRoute moduleKey="core">
													{setContainer(item.container, {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"wellDetails"}
											path={item.to.split("/").pop() + "/:wellcode"}
											element={
												<ProtectedModuleRoute moduleKey="core">
													{setContainer("wellDetails", {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"sharepointPreview"}
											path={item.to.split("/").pop() + "/:wellcode/preview"}
											element={
												<ProtectedModuleRoute moduleKey="core">
													{setContainer("sharepointPreview", {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>
									</>
								);
							} else if (item.container === "reporting") {
								route = (
									<>
										<Route
											key={item.key}
											path={item.to.split("/").pop()}
											element={
												<ProtectedModuleRoute moduleKey="reporting">
													{setContainer(item.container, {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>

										{/* My Reports Route */}
										<Route
											key={"myReports"}
											path={item.to.split("/").pop() + "/my-reports"}
											element={
												<ProtectedModuleRoute moduleKey="reporting">
													{setContainer("my-reports", {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										/>

										{/* Audit Stream Route */}
										<Route
											key={"auditStream"}
											path={item.to.split("/").pop() + "/audit-stream"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="audit"
												>
													<TenantSwitchBoundary>
														{setContainer("auditStream", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Incident Stream Route */}
										<Route
											key={"incidentStream"}
											path={item.to.split("/").pop() + "/incident-stream"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="incident_report"
												>
													<TenantSwitchBoundary>
														{setContainer("incidentStream", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Management of Change Stream Route */}
										<Route
											key={"mocStream"}
											path={item.to.split("/").pop() + "/moc-stream"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="moc"
												>
													<TenantSwitchBoundary>
														{setContainer("mocStream", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Opportunity for Improvement Stream Route */}
										<Route
											key={"ofiStream"}
											path={item.to.split("/").pop() + "/ofi-stream"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="ofi"
												>
													<TenantSwitchBoundary>
														{setContainer("ofiStream", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Ncr stream route */}
										<Route
											key={"ncrStream"}
											path={
												item.to.split("/").pop() + "/non-conformance-stream"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="non_conformance"
												>
													<TenantSwitchBoundary>
														{setContainer("ncrStream", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Incident Report Overview Route */}
										<Route
											key={"reportOverview"}
											path={
												item.to.split("/").pop() + "/incident-stream/:reportId"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="incident_report"
												>
													<TenantSwitchBoundary>
														{setContainer("reportOverview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Audit Report Overview Route */}
										<Route
											key={"reportOverview"}
											path={
												item.to.split("/").pop() + "/audit-stream/:reportId"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="audit"
												>
													<TenantSwitchBoundary>
														{setContainer("reportOverview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Management of Change Report Overview Route */}
										<Route
											key={"reportOverview"}
											path={item.to.split("/").pop() + "/moc-stream/:reportId"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="moc"
												>
													<TenantSwitchBoundary>
														{setContainer("reportOverview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Opportunity for Improvement Report Overview Route */}
										<Route
											key={"reportOverview"}
											path={item.to.split("/").pop() + "/ofi-stream/:reportId"}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="ofi"
												>
													<TenantSwitchBoundary>
														{setContainer("reportOverview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"reportOverview"}
											path={
												item.to.split("/").pop() +
												"/non-conformance-stream/:reportId"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="non_conformance"
												>
													<TenantSwitchBoundary>
														{setContainer("reportOverview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										{/* Audit sharepoint preview route */}
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/audit-stream/:reportId/preview"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="audit"
												>
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Incident sharepoint preview route */}
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/incident-stream/:reportId/preview"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="incident_report"
												>
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Management of Change sharepoint preview route */}
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/moc-stream/:reportId/preview"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="moc"
												>
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>

										{/* Opportunity for Improvement sharepoint preview route */}
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/ofi-stream/:reportId/preview"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="ofi"
												>
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
										<Route
											key={"sharepointPreview"}
											path={
												item.to.split("/").pop() +
												"/non-conformance-stream/:reportId/preview"
											}
											element={
												<ProtectedModuleRoute
													moduleKey="reporting"
													reportingType="non_conformance"
												>
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
									</>
								);
							} else if (item.container === "lesson-learned-stream") {
								route = (
									<>
										<Route
											key={item.key}
											path={item.to.split("/").pop()}
											element={
												<ProtectedModuleRoute moduleKey="lessons-learned">
													{setContainer(item.container, {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										></Route>
										<Route
											key={"reportOverview"}
											path={item.to.split("/").pop() + "/:reportId"}
											element={
												<ProtectedModuleRoute moduleKey="lessons-learned">
													{setContainer("reportOverview", {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											}
										></Route>
										<Route
											key={"sharepointPreview"}
											path={item.to.split("/").pop() + "/:reportId/preview"}
											element={
												<ProtectedModuleRoute moduleKey="project-delivered">
													<TenantSwitchBoundary>
														{setContainer("sharepointPreview", {
															...params,
															...item,
															userRole,
															user: user,
														})}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
									</>
								);
							} else if (item.container === "dashboard") {
								const dashboardPath = item.to.split("/").pop();
								const dashboardParams = {
									...params,
									...item,
									userRole,
									user: user,
								};
								route = (
									<>
										{/* Widget dashboard */}
										<Route
											key={item.key}
											path={dashboardPath}
											element={
												<ProtectedModuleRoute moduleKey={item.module_key}>
													{setContainer(item.container, dashboardParams)}
												</ProtectedModuleRoute>
											}
										/>
										{/* Full-screen project map, opened from the map widget */}
										<Route
											key={`${item.key}-project-map`}
											path={`${dashboardPath}/project-map`}
											element={
												<ProtectedModuleRoute moduleKey={item.module_key}>
													<TenantSwitchBoundary subject="project map">
														{setContainer(
															"dashboard-project-map",
															dashboardParams
														)}
													</TenantSwitchBoundary>
												</ProtectedModuleRoute>
											}
										/>
									</>
								);
							} else if (item.container === "helpCentre") {
								// Mounted for every signed-in user; the menu only hides the
								// entry from guests and examiners. The API sends hidden content
								// to editors only.
								route = (
									<Route
										key={item.key}
										path={item.to.split("/").pop()}
										element={<HelpCentreLayout />}
									>
										<Route index element={<HelpCentreHome />} />
										<Route path="topics" element={<HelpCentreTopics />} />
										<Route
											path="topics/:topicKey/:sectionKey?"
											element={<HelpCentreTopic />}
										/>
										{/* Editors only; the editor's code loads behind that check. */}
										<Route
											path="configuration"
											element={<HelpCentreConfigurationRoute />}
										/>
									</Route>
								);
							} else {
								route = (
									<Route
										key={item.key}
										path={item.to.split("/").pop()}
										element={
											// Check if this is a module-specific container that needs protection
											item.module_key ? (
												<ProtectedModuleRoute moduleKey={item.module_key}>
													{setContainer(item.container, {
														...params,
														...item,
														userRole,
														user: user,
													})}
												</ProtectedModuleRoute>
											) : (
												setContainer(item.container, {
													...params,
													...item,
													userRole,
													user: user,
												})
											)
										}
									/>
								);
							}
						}
						//Flat push
						if (item.requires_admin) {
							adminMenuRoutes.push({
								item: item,
								route: route,
							});
						} else {
							menuRoutes.push({
								item: item,
								route: route,
							});
						}
					}
					if (item.children && Array.isArray(item.children)) {
						traverse(item.children, parentPath + (item.to ?? ""));
					}
				});
			};
			traverse(allMenu);
			menuRoutes.push({
				item: null,
				route: <Route key="noAccess" path="*" element={<NoAccess />} />,
			});
			adminMenuRoutes.push({
				item: null,
				route: <Route key="noAccess" path="*" element={<NoAccess />} />,
			});
			setMenuRoutes(menuRoutes);
			setAdminMenuRoutes(adminMenuRoutes);
		}
	}, [userRole, user]);

	// set tenant role information
	useEffect(() => {
		const setTenant = () => {
			let tenantData: any = null;
			if (user) {
				let tenants = idTokenDecoded?.roles?.filter(
					(element: any) => element !== superAdminRole
				);
				if (
					tenants?.length > 0 ||
					idTokenDecoded?.roles?.includes(superAdminRole)
				) {
					Promise.all([
						getSpecificTenants(idTokenDecoded?.roles, tenants),
						getAppRoles(
							tenants,
							idTokenDecoded?.roles?.includes(superAdminRole)
								? superAdminRole
								: null
						),
						// A failed read loses only the remembered tenant.
						getLastTenant().catch(() => null),
					])
						.then((data: any) => {
							let tenantArray: any = data[1]
								.map((tenantRole: any) => {
									if (tenantRole.isEnabled) {
										return tenantRole;
									}
								})
								.filter(Boolean);
							let databaseTenantArray: any = data[0] ?? [];
							let combinedArray: any = [];
							tenantArray.forEach((appTenant: any) => {
								const foundObj = databaseTenantArray.find(
									(dbTenant: any) => dbTenant.uuid === appTenant.id
								);

								if (foundObj) {
									combinedArray.push({ ...appTenant, ...foundObj });
								} else {
									combinedArray.push(appTenant);
								}
							});

							combinedArray = combinedArray.sort((a: any, b: any) => {
								let aName = a.name || a.displayName;
								let bName = b.name || b.displayName;
								return aName.localeCompare(bName);
							});

							tenantData = combinedArray;
							// This browser's tenant, then the one remembered on the
							// server, then the master tenant. A fresh sign-in clears
							// this browser's tenant, so the remembered one wins then.
							const chosen = pickInitialTenant(tenantData, [
								localStorage.getItem("currentTenantKey"),
								data[2],
								import.meta.env.VITE_MASTER_TENANT_KEY,
							]);
							if (chosen) {
								localStorage.setItem("currentTenantKey", chosen.value);

								// Check if user is a tenant-admin
								const isTenantAdmin = includesIgnoreCase(
									chosen.tenant_admin,
									user.email
								);

								// Check if user is a tenant-member
								const isTenantMember = includesIgnoreCase(
									chosen.tenant_members,
									user.email
								);

								let roles = idTokenDecoded?.roles ?? [];

								// Add tenant-admin role if applicable
								if (isTenantAdmin) {
									roles = [...roles, "tenant-admin"];
								}

								// Add tenant-members role if applicable
								if (isTenantMember) {
									roles = [...roles, "tenant-members"];
								}

								setUserRole(roles);
								setUserData(user, roles);
								setDefaultTheme(chosen.default_theme);
								setThemeVariables(chosen.theme_settings);
							} else {
								setUserRole(idTokenDecoded?.roles ?? []);
							}
						})
						.catch((error: any) => {
							console.log(error);
						})
						.finally(() => {
							setTenantDataList(tenantData ?? []);
							setTenantList(tenantData ?? []);
						});
				} else {
					const roles = idTokenDecoded?.roles ?? [];
					setUserRole(roles);
					setUserData(user, roles);
					setTenantDataList([]);
					setTenantList([]);
				}
			}
		};

		setTenant();
		Emitter.on("tenantUpdated", () => {
			setTenant();
		});
	}, [user, idTokenDecoded]);

	useEffect(() => {
		const handlePopstate = () => {
			sessionStorage.removeItem("main-tabkey");
			sessionStorage.removeItem("admin-tabkey");
			sessionStorage.removeItem("secondary-tabkey");
			sessionStorage.removeItem("tabkey");
		};

		// Add event listener for popstate when component mounts
		window.addEventListener("popstate", handlePopstate);

		// Remove event listener when component unmounts
		return () => {
			window.removeEventListener("popstate", handlePopstate);
		};
	}, []);

	useEffect(() => {
		setLightDarkMode(theme, defaultTheme);

		if (defaultTheme === false && themeVariables) {
			setCustomTheme(themeVariables, theme);
		}
	}, [theme, defaultTheme, themeVariables]);

	// Dynamically inject the @font-face rule into the stylesheet
	useEffect(() => {
		const fontUrl = `${url}/fonts/CustomFont.woff2`;

		const styleSheet = document.styleSheets[0]; // Access the first stylesheet
		const fontFaceRule = `
			@font-face {
				font-family: "CustomFont";
				src: url("${fontUrl}") format("woff2");
			}
		`;

		styleSheet.insertRule(fontFaceRule, styleSheet.cssRules.length);
	}, []);

	return (
		<ConfigProvider
			theme={{
				hashed: false,
				algorithm: theme == "light" ? defaultAlgorithm : darkAlgorithm,
				token: {
					fontFamily: "CustomFont, sans-serif",
					colorBgContainerDisabled: "rgba(0, 0, 0, 0.04)",
				},
				components: {
					Table: {
						headerBorderRadius: 0,
					},
					Input: {
						borderRadius: 2,
						colorBgContainer: "transparent",
					},
					Select: {
						borderRadius: 2,
						colorBgContainer: "transparent",
					},
					Button: {
						borderRadius: 2,
					},
					Radio: {
						borderRadius: 2,
						colorBgContainer: "transparent",
					},
					InputNumber: {
						borderRadius: 2,
						colorBgContainer: "transparent",
					},
					Checkbox: {
						colorBgContainer: "transparent",
					},
					DatePicker: {
						borderRadius: 2,
					},
					Tooltip: {
						colorBgSpotlight: theme == "light" ? "#f8f8f8" : "#242424",
					},
					// Badge: {
					// 	colorText: theme == "light" ? "#000000" : "#ffffff",
					// },
				},
			}}
		>
			<ThemeContext.Provider
				value={{
					defaultTheme: defaultTheme,
					setDefaultTheme: setDefaultTheme,
					mode: theme,
					setMode: applyMode,
					themeVariables: themeVariables,
					setThemeVariables: setThemeVariables,
				}}
			>
				<div className="main-page">
					<Router>
						<Location onChange={onLocationChange}></Location>
						{shouldRedirect ? <Navigate to="" /> : <></>}
						<Routes>
							<Route key={"login"} path="" element={<LoginPage />} />
							<Route
								key={"loginsuccess"}
								path="loginsuccess"
								element={<LoginSuccess />}
							/>
							{isLogin ? (
								<>
									{" "}
									<Route
										path=""
										key="home"
										element={
											<Home
												tenants={tenantDataList}
												userInfo={{ user, userRole }}
												currentLocation={currentLocation}
												menu={[...menuRoutes, ...adminMenuRoutes]}
											/>
										}
									>
										{menuRoutes.map((menuItem: any) => (
											<Fragment key={menuItem.item?.key ?? "noAccess"}>
												{menuItem.route}
											</Fragment>
										))}
										{adminMenuRoutes.map((menuItem: any) => (
											<Fragment key={menuItem.item?.key ?? "noAccess"}>
												{menuItem.route}
											</Fragment>
										))}
									</Route>
								</>
							) : (
								<></>
							)}
						</Routes>
					</Router>
					{alert && (
						<Alert
							className={
								alert?.top ? "alert-message-box-top" : "alert-message-box"
							}
							type={alert?.type}
							message={alert?.message}
							description={alert?.description}
							showIcon
							closable={alert?.closable ?? false}
							afterClose={() => setAlert(null)}
						/>
					)}
				</div>
			</ThemeContext.Provider>
		</ConfigProvider>
	);
};

export default App;
